import React, { useState, useEffect } from 'react';
import { adminPostService } from '../../../services/adminPostService';
import { Loader2, Plus, Pencil, Trash2, Search, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Toaster, toast } from 'sonner';

const PostIndex = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [pagination, setPagination] = useState({});

    useEffect(() => {
        fetchPosts();
    }, []);

    const fetchPosts = async (page = 1) => {
        setLoading(true);
        try {
            const response = await adminPostService.getPosts({ page });
            setPosts(response.data.data);
            setPagination(response.data);
        } catch (error) {
            console.error("Failed to fetch posts", error);
            toast.error("Failed to load posts");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (post) => {
        if (window.confirm(`Are you sure you want to delete "${post.title}"?`)) {
            try {
                await adminPostService.deletePost(post.id);
                toast.success("Post deleted successfully");
                fetchPosts(pagination.current_page);
            } catch (error) {
                console.error("Failed to delete", error);
                toast.error("Failed to delete post");
            }
        }
    };

    const filteredPosts = posts.filter(post =>
        post.title.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="p-4 md:p-8 font-sans text-[#31415A] min-w-0">
            <Toaster position="top-right" />

            <div className="flex flex-col gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-semibold text-[#31415A]">Posts Management</h1>
                    <p className="text-sm text-gray-500 mt-1">Manage your blog posts and content here.</p>
                </div>
                <Link
                    to="/admin/posts/create"
                    className="flex items-center justify-center gap-2 bg-[#31415A] text-[#F7F1CD] px-4 py-2.5 rounded-lg hover:shadow-lg transition-all font-medium w-full md:w-auto md:self-end"
                >
                    <Plus className="w-4 h-4" />
                    Create Post
                </Link>
            </div>

            {loading ? (
                <div className="flex justify-center items-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
            ) : posts.length === 0 ? (
                <div className="bg-white p-12 rounded-xl border border-gray-100 text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-400">
                        <FileText className="w-8 h-8" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900">No posts found</h3>
                    <p className="text-gray-500 mt-1 mb-6 max-w-sm mx-auto">Get started by creating a new blog post for your audience.</p>
                    <Link to="/admin/posts/create" className="bg-[#31415A] text-[#F7F1CD] px-6 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition-opacity">
                        Create Post
                    </Link>
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-gray-50 text-gray-500 font-medium border-b border-gray-100">
                                <tr>
                                    <th className="px-6 py-4">Title</th>
                                    <th className="px-6 py-4">Status</th>
                                    <th className="px-6 py-4">Author</th>
                                    <th className="px-6 py-4">Date</th>
                                    <th className="px-6 py-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {filteredPosts.map((post) => (
                                    <tr key={post.id} className="hover:bg-gray-50/50 transition-colors">
                                        <td className="px-6 py-4 font-medium text-[#31415A]">
                                            {post.title}
                                        </td>
                                        <td className="px-6 py-4">
                                            {post.is_published ? (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Published</span>
                                            ) : (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">Draft</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-gray-600">{post.author || 'Admin'}</td>
                                        <td className="px-6 py-4 text-gray-600">
                                            {post.created_at ? new Date(post.created_at).toLocaleDateString() : '-'}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <Link
                                                    to={`/admin/posts/${post.id}/edit`}
                                                    className="p-2 text-gray-400 hover:text-[#31415A] hover:bg-gray-100 rounded-full transition-colors"
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </Link>
                                                <button
                                                    onClick={() => handleDelete(post)}
                                                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Pagination for Admin */}
            {pagination.last_page > 1 && (
                <div className="mt-6 flex flex-wrap justify-center gap-2">
                    <button
                        onClick={() => fetchPosts(pagination.current_page - 1)}
                        disabled={pagination.current_page === 1}
                        className="px-4 py-2 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-50 text-sm font-medium transition-colors"
                    >
                        Previous
                    </button>
                    <span className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-100 rounded">
                        Page {pagination.current_page} of {pagination.last_page}
                    </span>
                    <button
                        onClick={() => fetchPosts(pagination.current_page + 1)}
                        disabled={pagination.current_page === pagination.last_page}
                        className="px-4 py-2 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-50 text-sm font-medium transition-colors"
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

export default PostIndex;
